import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import './Selector.css';

const Selector = ({ label, value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const containerRef = useRef(null);
  const triggerRef = useRef(null);

  // Position the fixed dropdown relative to the trigger element
  const updateDropdownPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownStyle({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  const handleOpen = () => {
    updateDropdownPosition();
    setIsOpen(v => !v);
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Reposition on scroll or resize while open
  useEffect(() => {
    if (!isOpen) return;
    const handler = () => updateDropdownPosition();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [isOpen, updateDropdownPosition]);

  return (
    <div className="selector-container" ref={containerRef}>
      {label && <p className="selector-label">{label}</p>}

      <div
        ref={triggerRef}
        className={`selector-trigger ${isOpen ? 'selector-open' : ''}`}
        onClick={handleOpen}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOpen(); }
          if (e.key === 'Escape') setIsOpen(false);
        }}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span>{value}</span>
        <ChevronDown size={14} className="selector-chevron" />
      </div>

      {isOpen && (
        <div
          className="selector-dropdown"
          style={dropdownStyle}
          role="listbox"
        >
          {options.map((opt) => (
            <div
              key={opt}
              className={`selector-option ${value === opt ? 'selected' : ''}`}
              onClick={() => {
                onChange(opt);
                setIsOpen(false);
              }}
              role="option"
              aria-selected={value === opt}
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Selector;